import { PersonalShelf, PersonalShelves, PLAYLIST_INIT_OFFSET, PLAYLIST_LIMIT } from '@jwp/ott-common/src/constants';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { generatePlaylistPlaceholder } from '@jwp/ott-common/src/utils/collection';
import { isTruthyCustomParamValue } from '@jwp/ott-common/src/utils/common';
import { isScheduledOrLiveMedia } from '@jwp/ott-common/src/utils/liveEvent';
import type { Content } from '@jwp/ott-common/types/config';
import type { Playlist } from '@jwp/ott-common/types/playlist';
import { useEffect } from 'react';
import { useQueries, UseQueryOptions, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';

const placeholderData = generatePlaylistPlaceholder(30);

export type UsePlaylistResult = {
  data: Playlist | undefined;
  isLoading: boolean;
  isSuccess?: boolean;
  error?: unknown;
}[];

const usePlaylists = (content: Content[], rowsToLoad: number | undefined = undefined, refreshKey?: number) => {
  const queryClient = useQueryClient();
  const apiService = getModule(ApiService);
  const { pathname } = useLocation();

  const favorites = useFavoritesStore((state) => state.getPlaylist());
  const watchHistory = useWatchHistoryStore((state) => state.getPlaylist());
  const contentStore = useContentStateStore((s) => s.rows);
  const setShelfState = useContentStateStore((s) => s.setRowState);
  const playlistQueries = useQueries<UseQueryOptions<Playlist | undefined, unknown, Playlist | undefined>[]>(
    content.map(({ contentId, type, featured }, index) => {
      const shelfRow = contentId ? contentStore[`${pathname}:${contentId}`] : undefined;

      const hasPrevLoadedItems = shelfRow?.totalLoaded !== undefined && shelfRow.totalLoaded > 0;
      const page_limit = hasPrevLoadedItems ? ((shelfRow?.pageOffset ?? 0) + PLAYLIST_LIMIT).toString() : PLAYLIST_LIMIT.toString();
      const page_offset = (hasPrevLoadedItems ? 1 : PLAYLIST_INIT_OFFSET).toString();

      return {
        enabled: !!contentId && (!rowsToLoad || rowsToLoad > index) && !PersonalShelves.some((pType) => pType === type),
        queryKey: ['playlist', contentId],
        queryFn: async (): Promise<Playlist | undefined> => {
          const playlist = await apiService.getPlaylistById(contentId, { page_limit, page_offset }, featured);

          if (playlist?.links?.next && playlist.feedid) {
            setShelfState(pathname, playlist.feedid, {
              hasNext: true,
            });
          }
          playlist?.playlist?.forEach((playlistItem) => {
            queryClient.setQueryData(['media', playlistItem.mediaid], playlistItem);
          });

          return playlist;
        },
        placeholderData: contentId ? placeholderData : undefined,
        refetchInterval: (data: Playlist | undefined) => {
          if (!data) return false;
          const autoRefetch = isTruthyCustomParamValue(data.refetch) || data.playlist.some(isScheduledOrLiveMedia);
          return autoRefetch ? 1000 * 30 : false;
        },
        retry: false,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
      };
    }),
  );

  useEffect(() => {
    playlistQueries.forEach((query) => {
      const data = query.data as Playlist | undefined;
      if (data && data.playlist.some(isScheduledOrLiveMedia)) {
        query.refetch?.();
      }
    });
  }, [refreshKey]);

  const result: UsePlaylistResult = content.map(({ type }, index) => {
    if (type === PersonalShelf.Favorites) return { data: favorites, isLoading: false, isSuccess: true };
    if (type === PersonalShelf.ContinueWatching) return { data: watchHistory, isLoading: false, isSuccess: true };

    const { data, isLoading, isSuccess, error } = playlistQueries[index];

    return {
      data,
      isLoading,
      isSuccess,
      error,
    };
  });

  return result;
};

export default usePlaylists;
